import React, { useEffect, useRef, useState } from 'react';
import { BarChart3, Cookie, ShieldCheck, X } from 'lucide-react';
import { setAnalyticsConsent } from '../firebase';
import {
  COOKIE_CONSENT_CHANGED_EVENT,
  COOKIE_CONSENT_STORAGE_KEY,
  getCookieConsent,
  OPEN_COOKIE_SETTINGS_EVENT,
  saveCookieConsent,
} from '../utils/cookieConsent';

const choiceButtonClass =
  'inline-flex min-h-12 w-full items-center justify-center rounded-xl border border-[#D7B55B] bg-[#FFF9EC] px-4 py-3 text-center text-sm font-semibold text-[#183F30] shadow-sm transition-colors hover:bg-[#F3DE9F] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F3C95D] focus-visible:ring-offset-2 focus-visible:ring-offset-[#123C2D]';

const modalChoiceButtonClass =
  'inline-flex min-h-12 w-full items-center justify-center rounded-xl border border-[#B99A55] bg-white px-4 py-3 text-center text-sm font-semibold text-[#183F30] transition-colors hover:bg-[#F7EED7] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0F3D2C] focus-visible:ring-offset-2';

function runAnalyticsConsentUpdate(analytics) {
  Promise.resolve(setAnalyticsConsent(analytics)).catch((error) => {
    if (process.env.NODE_ENV !== 'production') {
      console.error('Az Analytics hozzájárulás frissítése sikertelen:', error);
    }
  });
}

export default function CookieConsent() {
  const [storedConsent, setStoredConsent] = useState(() => getCookieConsent());
  const [bannerVisible, setBannerVisible] = useState(() => getCookieConsent() === null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [analyticsSelected, setAnalyticsSelected] = useState(
    () => getCookieConsent()?.analytics === true
  );
  const [statusMessage, setStatusMessage] = useState('');
  const bannerHeadingRef = useRef(null);
  const modalRef = useRef(null);
  const modalHeadingRef = useRef(null);
  const openerRef = useRef(null);

  useEffect(() => {
    runAnalyticsConsentUpdate(storedConsent?.analytics === true);
  }, [storedConsent]);

  useEffect(() => {
    if (bannerVisible && !settingsOpen) {
      bannerHeadingRef.current?.focus();
    }
  }, [bannerVisible, settingsOpen]);

  useEffect(() => {
    const openSettings = () => {
      openerRef.current = document.activeElement;
      const currentConsent = getCookieConsent();
      setAnalyticsSelected(currentConsent?.analytics === true);
      setSettingsOpen(true);
    };

    const consentChanged = (event) => {
      const consent = event.detail || getCookieConsent();
      setStoredConsent(consent);
      setBannerVisible(consent === null);
    };

    const storageChanged = (event) => {
      if (event.key === null || event.key === COOKIE_CONSENT_STORAGE_KEY) {
        const consent = getCookieConsent();
        setStoredConsent(consent);
        setBannerVisible(consent === null);
      }
    };

    window.addEventListener(OPEN_COOKIE_SETTINGS_EVENT, openSettings);
    window.addEventListener(COOKIE_CONSENT_CHANGED_EVENT, consentChanged);
    window.addEventListener('storage', storageChanged);

    return () => {
      window.removeEventListener(OPEN_COOKIE_SETTINGS_EVENT, openSettings);
      window.removeEventListener(COOKIE_CONSENT_CHANGED_EVENT, consentChanged);
      window.removeEventListener('storage', storageChanged);
    };
  }, []);

  useEffect(() => {
    if (!settingsOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    modalHeadingRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setSettingsOpen(false);
        window.requestAnimationFrame(() => openerRef.current?.focus());
        return;
      }

      if (event.key !== 'Tab') return;

      const focusableElements = modalRef.current?.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
      );
      if (!focusableElements?.length) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (
        event.shiftKey &&
        (document.activeElement === firstElement ||
          document.activeElement === modalHeadingRef.current)
      ) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [settingsOpen]);

  const closeSettings = () => {
    setSettingsOpen(false);
    window.requestAnimationFrame(() => openerRef.current?.focus());
  };

  const saveChoice = (analytics) => {
    const returnFocusTarget = openerRef.current;
    const consent = saveCookieConsent(analytics);
    setStoredConsent(consent);
    setBannerVisible(false);
    setSettingsOpen(false);
    setAnalyticsSelected(consent.analytics);
    setStatusMessage(
      consent.analytics
        ? 'A szükséges és analitikai sütik engedélyezve.'
        : 'Csak a szükséges sütik engedélyezve.'
    );
    window.requestAnimationFrame(() => {
      if (returnFocusTarget?.isConnected) returnFocusTarget.focus();
    });
  };

  const openSettingsFromBanner = (event) => {
    openerRef.current = event.currentTarget;
    setAnalyticsSelected(storedConsent?.analytics === true);
    setSettingsOpen(true);
  };

  return (
    <>
      <p className="sr-only" role="status" aria-live="polite">
        {statusMessage}
      </p>

      {bannerVisible && !settingsOpen ? (
        <section
          className="fixed inset-x-0 bottom-0 z-[60] border-t border-[#D7B55B] bg-[#123C2D] text-[#FFF9EC] shadow-[0_-18px_50px_rgba(15,61,44,0.22)]"
          aria-labelledby="cookie-banner-title"
          aria-describedby="cookie-banner-description"
        >
          <div className="container-zinga py-5 md:py-6">
            <div className="grid items-end gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(560px,0.9fr)]">
              <div>
                <div className="flex items-center gap-3">
                  <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#F3C95D] text-[#123C2D]">
                    <Cookie className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <h2
                    id="cookie-banner-title"
                    ref={bannerHeadingRef}
                    tabIndex={-1}
                    className="font-display text-xl font-semibold outline-none md:text-2xl"
                  >
                    Süti- és adatvédelmi beállítások
                  </h2>
                </div>
                <p
                  id="cookie-banner-description"
                  className="mt-3 max-w-2xl text-sm leading-relaxed text-[#E7D9C2]"
                >
                  A webáruház működéséhez szükséges helyi tárolást használunk. Az összesített
                  látogatottsági statisztikát biztosító Firebase Analytics csak az Ön
                  kifejezett engedélye után indul el. További részletek az{' '}
                  <a
                    href="/privacy"
                    className="font-semibold text-white underline decoration-[#D7B55B] underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F3C95D]"
                  >
                    adatvédelmi tájékoztatóban
                  </a>
                  .
                </p>
                <div className="mt-3 flex flex-wrap gap-2 text-xs">
                  <span className="rounded-full border border-white/20 px-3 py-1.5">
                    Szükséges · mindig aktív
                  </span>
                  <span className="rounded-full border border-white/20 px-3 py-1.5">
                    Analitika · alapból kikapcsolva
                  </span>
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-3" aria-label="Süti választási lehetőségek">
                <button type="button" className={choiceButtonClass} onClick={() => saveChoice(true)}>
                  Összes elfogadása
                </button>
                <button type="button" className={choiceButtonClass} onClick={() => saveChoice(false)}>
                  Csak szükséges sütik
                </button>
                <button type="button" className={choiceButtonClass} onClick={openSettingsFromBanner}>
                  Beállítások
                </button>
              </div>
            </div>
          </div>
        </section>
      ) : null}

      {settingsOpen ? (
        <div
          className="fixed inset-0 z-[70] flex items-end justify-center bg-[#071E16]/70 p-0 backdrop-blur-sm sm:items-center sm:p-6"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeSettings();
          }}
        >
          <section
            ref={modalRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="cookie-settings-title"
            aria-describedby="cookie-settings-description"
            className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-t-3xl border border-[#D8C7A6] bg-[#FFF9EC] text-[#183F30] shadow-2xl sm:rounded-3xl"
          >
            <header className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-[#DED0B7] bg-[#FFF9EC]/95 px-5 py-5 backdrop-blur sm:px-7">
              <div>
                <p className="mb-1 text-xs font-semibold uppercase tracking-[0.18em] text-[#8A642E]">
                  Adatvédelmi beállítások
                </p>
                <h2
                  id="cookie-settings-title"
                  ref={modalHeadingRef}
                  tabIndex={-1}
                  className="font-display text-2xl font-semibold outline-none sm:text-3xl"
                >
                  Süti beállítások
                </h2>
              </div>
              <button
                type="button"
                onClick={closeSettings}
                className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[#CDBB99] bg-white transition-colors hover:bg-[#F3E8CD] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0F3D2C]"
                aria-label="Süti beállítások bezárása"
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </header>

            <div className="space-y-4 px-5 py-6 sm:px-7">
              <p id="cookie-settings-description" className="text-sm leading-relaxed text-[#4E6259]">
                Itt kiválaszthatja, mely adattárolási kategóriákat engedélyezi. Döntését
                később a lábléc „Süti beállítások” linkjével bármikor módosíthatja vagy
                visszavonhatja.
              </p>

              <article className="rounded-2xl border border-[#D8C7A6] bg-white p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex gap-3">
                    <span className="mt-0.5 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#D7E9DE] text-[#0F3D2C]">
                      <ShieldCheck className="h-5 w-5" aria-hidden="true" />
                    </span>
                    <div>
                      <h3 className="font-display text-lg font-semibold">Szükséges</h3>
                      <p className="mt-1 text-sm leading-relaxed text-[#596B62]">
                        A webáruház alapvető működéséhez, biztonságához és a süti-döntés
                        megjegyzéséhez szükséges. Nem kapcsolható ki.
                      </p>
                    </div>
                  </div>
                  <span className="shrink-0 rounded-full bg-[#D7E9DE] px-3 py-1.5 text-xs font-semibold text-[#0F3D2C]">
                    Mindig aktív
                  </span>
                </div>
              </article>

              <article className="rounded-2xl border border-[#D8C7A6] bg-white p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex gap-3">
                    <span className="mt-0.5 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#F3E8CD] text-[#744E1C]">
                      <BarChart3 className="h-5 w-5" aria-hidden="true" />
                    </span>
                    <div>
                      <label htmlFor="analytics-consent" className="font-display text-lg font-semibold">
                        Analitika
                      </label>
                      <p id="analytics-description" className="mt-1 text-sm leading-relaxed text-[#596B62]">
                        A Firebase Analytics segítségével összesített információt kapunk a
                        weboldal használatáról. Csak bekapcsolás után töltődik be és küld
                        adatot.
                      </p>
                    </div>
                  </div>
                  <label className="relative mt-1 inline-flex shrink-0 cursor-pointer items-center">
                    <input
                      id="analytics-consent"
                      type="checkbox"
                      className="peer sr-only"
                      checked={analyticsSelected}
                      onChange={(event) => setAnalyticsSelected(event.target.checked)}
                      aria-describedby="analytics-description"
                    />
                    <span className="h-7 w-12 rounded-full bg-[#C8C8C0] transition-colors peer-checked:bg-[#0F3D2C] peer-focus-visible:outline-none peer-focus-visible:ring-2 peer-focus-visible:ring-[#0F3D2C] peer-focus-visible:ring-offset-2 after:absolute after:left-1 after:top-1 after:h-5 after:w-5 after:rounded-full after:bg-white after:shadow after:transition-transform peer-checked:after:translate-x-5" />
                    <span className="sr-only">
                      {analyticsSelected ? 'Analitika bekapcsolva' : 'Analitika kikapcsolva'}
                    </span>
                  </label>
                </div>
              </article>

              <p className="rounded-xl bg-[#F3E8CD]/70 px-4 py-3 text-xs leading-relaxed text-[#5F543E]">
                A választást kizárólag az engedélyezett analitikai állapot és a mentés
                időpontja formájában tároljuk ezen az eszközön.
              </p>
            </div>

            <footer className="grid gap-3 border-t border-[#DED0B7] bg-[#F8F0DE] px-5 py-5 sm:grid-cols-3 sm:px-7">
              <button type="button" className={modalChoiceButtonClass} onClick={() => saveChoice(true)}>
                Összes elfogadása
              </button>
              <button type="button" className={modalChoiceButtonClass} onClick={() => saveChoice(false)}>
                Csak szükséges sütik
              </button>
              <button
                type="button"
                className={modalChoiceButtonClass}
                onClick={() => saveChoice(analyticsSelected)}
              >
                Beállítások mentése
              </button>
            </footer>
          </section>
        </div>
      ) : null}
    </>
  );
}
